import React from "react";
import { Card } from "antd5";
import { useLocation } from "wouter";

import { CreditAccessWrapper } from "components/paywall/CreditAccessWrapper";
import Button from "components/ui/button/Button";
import NewFeatureTag from "components/ui/new_feature_tag/NewFeatureTag";
import { MAY_4TH_ACTIVATION, useVariableValue } from "lib/featureFlags";
import { useSubscription } from "lib/hooks/subscription";
import { EventNames, useTracking } from "lib/tracking";
import { grey0 } from "../../lib/themes/colors";

import css from "./AutoQualifyCard.module.scss";

type Props = {
  opportunityId: string;
  bidId: string | undefined;
};
export function AutoQualifyCard({ opportunityId, bidId }: Props) {
  const subscription = useSubscription();
  const hasBidsDataType = subscription.hasDataTypes("BIDS");
  const [, setLocation] = useLocation();

  const { logEvent } = useTracking();

  const isMayActivationEnabled = useVariableValue(MAY_4TH_ACTIVATION, false);

  // Do not show if it's a new bid, they don't have data access & may
  // activation feature is not enabled
  if (!bidId && !hasBidsDataType && !isMayActivationEnabled) {
    return null;
  }

  const cardContent = (): JSX.Element => {
    if (bidId) {
      return (
        <>
          <p> Tender documents have been analysed and the report is available.</p>
          <div>
            <Button
              onClick={() => {
                logEvent(EventNames.qualificationReportActioned, {
                  "Report status": "Existing",
                  "Opportunity ID": opportunityId,
                });
                setLocation(`/${opportunityId}/bid_qualification/${bidId}`);
              }}
              buttonType="primary"
              iconProps={{ color: grey0, size: 16 }}
              icon="stars2"
              style={{ width: "fit-content", padding: "0px 12px 0px 8px" }}
              justifyContent="flex-start"
            >
              View bid / no-bid report
            </Button>
          </div>
        </>
      );
    } else {
      return (
        <>
          <p>
            Quickly decide whether to bid. Stotles AI analyses tender documents and generates a bid
            / no-bid report. Upload your documents to get started.
          </p>
          <div>
            <CreditAccessWrapper
              requiredDataType="BIDS"
              creditType="BID"
              contextSource="Bid studio"
            >
              <Button
                buttonType="primary"
                onClick={() => {
                  logEvent(EventNames.qualificationReportActioned, {
                    "Report status": "New",
                    "Opportunity ID": opportunityId,
                  });
                  setLocation(`/${opportunityId}/bid_qualification/`);
                }}
                iconProps={{ color: grey0, size: 16 }}
                icon="stars2"
                style={{ width: "fit-content", padding: "0px 12px 0px 8px" }}
                justifyContent="flex-start"
              >
                Generate report
              </Button>
            </CreditAccessWrapper>
          </div>
        </>
      );
    }
  };

  return (
    <div className={css.cardContainer}>
      <Card className={`${css.upperCard}`}>
        <div className={css.upperCardContent}>
          <h2 className={css.mediumTitle}>Bid / no-bid report</h2>
          <NewFeatureTag NewFeature="bids" />
        </div>
      </Card>
      <Card className={css.lowerCard}>
        <div className={css.lowerCardContent}>{cardContent()}</div>
      </Card>
    </div>
  );
}
